import { isServerSide } from '@r1-frontend/shared/helpers/ssr';
import { getLocalStorageByKey, setLocalStorageByKey } from '@r1-frontend/shared/helpers/storage';

import { LS_CART_KEY } from '../constants';

import { ICartItem, ICartState } from './types';

const isValidCartItem = (value: unknown): value is ICartItem => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    if (!('id' in value) || typeof value.id !== 'string') {
        return false;
    }

    if (!('productId' in value) || typeof value.productId !== 'string') {
        return false;
    }

    if ('variantId' in value && value.variantId !== undefined && typeof value.variantId !== 'string') {
        return false;
    }

    if (!('title' in value) || typeof value.title !== 'string') {
        return false;
    }

    if ('imageUrl' in value && value.imageUrl !== undefined && typeof value.imageUrl !== 'string') {
        return false;
    }

    if (!('price' in value) || typeof value.price !== 'number') {
        return false;
    }

    if (!('quantity' in value) || typeof value.quantity !== 'number') {
        return false;
    }

    if (!('isSelected' in value) || typeof value.isSelected !== 'boolean') {
        return false;
    }

    if (!('isAvailable' in value) || typeof value.isAvailable !== 'boolean') {
        return false;
    }

    return !('createdAt' in value) || value.createdAt === undefined || typeof value.createdAt === 'string';
};

const isValidCartState = (value: unknown): value is ICartState => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    if (!('localCartId' in value) || (value.localCartId !== null && typeof value.localCartId !== 'string')) {
        return false;
    }

    if (!('items' in value) || !Array.isArray(value.items) || !value.items.every(isValidCartItem)) {
        return false;
    }

    return 'isHydrated' in value && typeof value.isHydrated === 'boolean';
};

const normalizePersistedCart = (cart: ICartState): ICartState => ({
    ...cart,
    items: cart.items.filter(item => item.quantity > 0),
});

export const loadCartFromStorage = (): ICartState | null => {
    const storedCart = getLocalStorageByKey(LS_CART_KEY);

    if (!isValidCartState(storedCart)) {
        return null;
    }

    return normalizePersistedCart(storedCart);
};

export const saveCartToStorage = (cart: ICartState): void => {
    setLocalStorageByKey(LS_CART_KEY, normalizePersistedCart(cart));
};

export const clearCartStorage = (): void => {
    if (!isServerSide()) {
        localStorage.removeItem(LS_CART_KEY);
    }
};
